import { useState } from 'react';
import {
  Button,
  DropDown,
  Flex,
  FlexColumn,
  FormControls,
  IconButton,
  Radio,
  Switch,
  TextField,
} from '@elementary/common-ui/components';
import {
  FormControlLabel,
  RadioGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { ReactComponent as TrashIcon } from '@elementary/common-ui/assets/trash.svg';
import { Controller, UseFormReturn, useFieldArray } from 'react-hook-form';
import { AddTestForm } from 'shared/types';
import { enforcementLevelOptions } from '../constants';
import SeveritySelector from '../SeveritySelector';
import TableTestDescriptionField from './shared/TableTestDescriptionField';
import TestMetadataForm from './shared/TestMetadataForm';

type Props = {
  methods: UseFormReturn<AddTestForm, any, undefined>;
};

const TablesDataContract = ({ methods }: Props) => {
  const [expanded, setExpanded] = useState(true);
  const baselineMode = methods.watch('baselineMode') || 'complete';

  const { fields, append, remove } = useFieldArray({
    control: methods.control,
    name: 'baselineColumns',
  });

  return (
    <FormControls.TestAccordion
      summary="Data contract"
      expanded={expanded}
      onChange={(_, val) => setExpanded(val)}
    >
      <FlexColumn sx={{ gap: 3, p: 3 }}>
        <FormControls.FieldsWrapper>
          <Typography sx={{ fontWeight: 'bold' }}>
            Test configuration
          </Typography>
          <Controller
            name="severity"
            control={methods.control}
            render={({ field }) => <SeveritySelector {...field} />}
            defaultValue={{ type: 'error' }}
          />
        </FormControls.FieldsWrapper>

        <FormControls.Divider />

        <FormControls.FieldsWrapper>
          <Typography sx={{ fontWeight: 'bold' }}>Baseline mode</Typography>
          <Controller
            name="baselineMode"
            control={methods.control}
            defaultValue="complete"
            render={({ field }) => (
              <RadioGroup
                value={field.value || 'complete'}
                onChange={(e) => field.onChange(e.target.value)}
              >
                <FormControlLabel
                  value="complete"
                  control={<Radio />}
                  label="Complete — use full table schema as baseline"
                />
                <FormControlLabel
                  value="manual"
                  control={<Radio />}
                  label="Manual — define expected columns"
                />
              </RadioGroup>
            )}
          />
        </FormControls.FieldsWrapper>

        {baselineMode === 'manual' && (
          <FormControls.FieldsWrapper>
            <Typography sx={{ fontWeight: 'bold' }}>
              Expected columns
            </Typography>
            <FlexColumn sx={{ gap: 1 }}>
              {fields.map((field, index) => (
                <Flex key={field.id} sx={{ gap: 2, alignItems: 'center' }}>
                  <TextField
                    placeholder="Column name"
                    {...methods.register(
                      `baselineColumns.${index}.column_name`,
                    )}
                  />
                  <TextField
                    placeholder="Data type"
                    {...methods.register(`baselineColumns.${index}.data_type`)}
                  />
                  <Flex sx={{ gap: 1, alignItems: 'center', flexShrink: 0 }}>
                    <Switch
                      size="small"
                      checked={
                        methods.watch(`baselineColumns.${index}.is_nullable`) ??
                        true
                      }
                      onChange={(e) =>
                        methods.setValue(
                          `baselineColumns.${index}.is_nullable`,
                          e.target.checked,
                        )
                      }
                    />
                    <Typography variant="body2">Nullable</Typography>
                  </Flex>
                  <IconButton size="small" onClick={() => remove(index)}>
                    <TrashIcon />
                  </IconButton>
                </Flex>
              ))}
              <Flex>
                <Button
                  variant="outlined"
                  size="extraSmall"
                  onClick={() =>
                    append({
                      column_name: '',
                      data_type: '',
                      is_nullable: true,
                    })
                  }
                >
                  Add column
                </Button>
              </Flex>
            </FlexColumn>
          </FormControls.FieldsWrapper>
        )}

        <FormControls.Divider />

        <FormControls.FieldsWrapper>
          <Controller
            name="enforcementLevel"
            control={methods.control}
            defaultValue="all"
            render={({ field }) => (
              <FormControls.Row>
                <Tooltip
                  title={
                    <>
                      <div>
                        All changes -- fail on any schema deviation, including
                        new columns.
                      </div>
                      <div>
                        Breaking changes only -- fail on column removals and
                        type/nullability changes, but tolerate new columns.
                      </div>
                      <div>
                        Downstream impact only -- fail only when the changed
                        column has downstream dependencies.
                      </div>
                      <div>
                        Critical downstream impact only -- fail only when the
                        changed column has critical downstream dependencies.
                      </div>
                    </>
                  }
                >
                  <Typography sx={{ fontWeight: 'bold', cursor: 'help' }}>
                    Enforcement level
                  </Typography>
                </Tooltip>
                <DropDown
                  size="medium"
                  options={enforcementLevelOptions}
                  value={field.value || 'all'}
                  onChange={field.onChange}
                />
              </FormControls.Row>
            )}
          />
        </FormControls.FieldsWrapper>

        <FormControls.Divider />

        <TableTestDescriptionField
          defaultValue="Validates that the table schema conforms to the defined data contract"
          methods={methods}
        />

        <TestMetadataForm methods={methods} />
      </FlexColumn>
    </FormControls.TestAccordion>
  );
};

export default TablesDataContract;
